"use client"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Crown, Check } from "lucide-react"
import Link from "next/link"
import { isFreeMode } from "@/lib/feature-flags"

interface SubscriptionPromptProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  message?: string
}

export function SubscriptionPrompt({ 
  isOpen, 
  onClose, 
  title = "Upgrade to Premium",
  message = "You've reached your free tier limit"
}: SubscriptionPromptProps) {
  if (isFreeMode) {
    return null
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-full">
            <Crown className="h-8 w-8 text-white" />
          </div>
          <DialogTitle className="text-center text-xl font-semibold">
            {title}
          </DialogTitle>
          <DialogDescription className="text-center text-muted-foreground">
            {message}. Upgrade to BookSip Premium for unlimited access to all books and content.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="font-medium text-sm">Unlimited Book Access</p>
                <p className="text-xs text-muted-foreground">Access any number of books</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="font-medium text-sm">All Content Formats</p>
                <p className="text-xs text-muted-foreground">Text summaries, audio podcasts, downloads</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="font-medium text-sm">Lifetime Access</p>
                <p className="text-xs text-muted-foreground">One-time payment, no recurring fees</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-950/30 dark:to-indigo-950/30 rounded-lg p-4 border border-purple-200 dark:border-purple-800">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-purple-900 dark:text-purple-100">BookSip Premium</p>
                <p className="text-sm text-purple-700 dark:text-purple-300">Lifetime Access</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-purple-900 dark:text-purple-100">$29</p>
                <p className="text-xs text-purple-700 dark:text-purple-300">One-time payment</p>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={onClose} className="w-full sm:w-auto">
            Maybe Later
          </Button>
          <Button asChild className="w-full sm:w-auto bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700">
            <Link href="/subscription">
              Upgrade Now
            </Link>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

